import { useReducer, useEffect } from 'react';
import useConnectFitness from './useConnectFitness';
import queryString from 'query-string';
import { PageMetaContext } from '../../context/PageMetaContext';
import { useNotNullableContext } from '../useNotNullableContext';
import { useTranslation } from 'react-i18next';
import { getBackendErrorMessage } from '../../utils/getBackendErrorMessage';

export type ConnectionStatus =
  | 'unconnected'
  | 'connecting'
  | 'success'
  | 'failed'
  | 'connected';

interface State {
  status: ConnectionStatus;
  code?: string;
  scope?: string;
  error?: string;
}

function connectionReducer(state: State, action: any): State {
  switch (action.type) {
    case 'Connect':
      return { status: 'connecting', code: action.code, scope: action.scope };
    case 'ConnectionSuccess':
      return { ...state, status: 'success' };
    case 'ConnectionFailed':
      return { ...state, status: 'failed', error: action.error };
    case 'NoConnection':
    default:
      return { ...state, status: 'unconnected' };
  }
}

function initializeState(isConnected: boolean): State {
  return {
    code: undefined,
    scope: undefined,
    status: isConnected ? 'connected' : 'unconnected',
  };
}

export default function useFitnessConnection(isConnected: boolean) {
  const { pageShortName } = useNotNullableContext(PageMetaContext);
  const [state, dispatch] = useReducer(
    connectionReducer,
    initializeState(isConnected),
  );
  const { status, code, scope } = state;
  const [connectFitness] = useConnectFitness({ code, scope });
  const { t } = useTranslation('common');

  useEffect(() => {
    const { code, scope } = queryString.parse(window.location.search);
    const hasConnectionData = Boolean(code && scope);
    if (hasConnectionData) {
      window.history.replaceState({}, '', pageShortName);
      if (status === 'unconnected') {
        dispatch({ type: 'Connect', code, scope });
      }
    }
  }, [status, pageShortName]);

  useEffect(() => {
    if (status === 'connecting') {
      connectFitness()
        .then(() => dispatch({ type: 'ConnectionSuccess' }))
        .catch(responseError => {
          const error = getBackendErrorMessage(responseError);
          return dispatch({
            type: 'ConnectionFailed',
            error: t(error),
          });
        });
    }
  }, [status, connectFitness, t]);

  return state;
}
